// Single source of truth for the words in ReviewingPitches (portal DOM) and
// ReviewingPitchesPDF (print). Rendered by ../documents/DocContent.jsx and
// ../pdf/PdfContent.jsx — the block shape is documented in DocContent.jsx.
// pdf* fields and the dom:/pdf: switches mark where the print copy
// deliberately diverges from the portal copy.

export const docTitle = "GNF reviewing pitches";

export const cover = {
  eyebrow: "Review · 04",
  number: "04",
  title: "Reviewing pitches",
  summary: "How the Review tab works, what each button does, and how to think about scoring when a term sheet isn't the point.",
  accent: "tangerine",
};

export const blocks = [
  {
    type: "lede",
    text: "Rating pitches is the job. Here's how the Review tab works, what each button actually does, and how we think about scoring when a term sheet isn't the point.",
    pdfText: "Rating pitches is the job. Here's how the Review tab works, what each button actually does, and how to think about scoring when a term sheet isn't the point.",
  },
  { type: "heading", level: 1, text: "Where reviewing lives" },
  {
    type: "p",
    runs: [
      "When you log into the portal, you land on the ",
      { b: "Review pitches" },
      " tab by default. Everything submitted to your chapter shows up there as a card until you rate it. No queue-opening, no rounds. Pitches flow in continuously and you work through them on your own time.",
    ],
    pdfRuns: [
      "When you log into the portal, you land on the Review pitches tab by default. Everything submitted to your chapter shows up as a card until you rate it. No queue-opening, no rounds. Pitches flow in continuously and you work through them on your own time.",
    ],
  },
  { type: "heading", level: 1, text: "Reading the pitch cards" },
  {
    type: "p",
    runs: [
      "Each pitch is a card with the business name, founder, submission date, chapter, quarter, and a short ",
      { b: "AI-generated overview" },
      " of what the pitch is about. The overview is meant to help you triage the list, not to replace the application. Anything that catches your eye deserves a real read.",
    ],
    pdfRuns: [
      "Each pitch is a card with the business name, founder, submission date, chapter, quarter, and a short AI-generated overview of what the pitch is about. The overview is meant to help you triage the list, not to replace the application. Anything that catches your eye deserves a real read.",
    ],
  },
  { type: "p", runs: ["The stripe on the left edge tells you your review status at a glance:"] },
  {
    type: "list",
    items: [
      { runs: [{ b: "Magenta." }, " You haven't reviewed it yet."] },
      { runs: [{ b: "Gold." }, " You marked it a Favorite."] },
      { runs: [{ b: "Aqua." }, " You gave it a Consideration."] },
      { runs: [{ b: "Gray." }, " You rated it a Pass or Ineligible."] },
    ],
  },
  { type: "heading", level: 1, text: "Opening a pitch", pdfBreak: true },
  {
    type: "p",
    runs: [
      "Click any card and you'll drop into the full application the founder submitted, with every question they answered in their own words. Two buttons sit at the top of the detail panel:",
    ],
  },
  {
    type: "list",
    items: [
      {
        runs: [{ b: "▶ Watch Pitch Video." }, " Opens the pitch video in a new tab."],
        pdfRuns: [{ b: "Watch Pitch Video." }, " Opens the pitch video in a new tab."],
      },
      {
        runs: [{ b: "↗ Visit Website." }, " Opens the applicant's website, if they gave us one."],
        pdfRuns: [{ b: "Visit Website." }, " Opens the applicant's website, if they gave us one."],
      },
    ],
  },
  {
    type: "callout",
    label: "If the video won't play",
    runs: [
      "Every so often an applicant uploads to Google Drive without flipping the share settings to \"anyone with the link.\" You'll click Watch and hit a permission wall. Don't rate the pitch yet. Slack your Chapter Director so they can reach out and get the permissions fixed. We don't want a founder penalized because Drive defaults bit them.",
    ],
  },
  { type: "heading", level: 1, text: "Filtering the list" },
  {
    type: "p",
    runs: [
      "The filters along the top of the Review tab keep the queue manageable. A few patterns worth using:",
    ],
  },
  {
    type: "list",
    items: [
      {
        runs: [
          { b: "Show only unreviewed." },
          " Use the Review status filter. Fastest way to work through a new batch without re-scrolling past pitches you've already rated.",
        ],
      },
      {
        runs: [
          { b: "Pick a single quarter." },
          " Useful before your quarterly meeting when you only want to see the cycle you're about to discuss.",
        ],
      },
      {
        runs: [
          { b: "Hide Passes." },
          " Flip this on after your first pass and the remaining view is your Favorites and Considerations, which is typically what you want to revisit before the meeting.",
        ],
        pdfRuns: [
          { b: "Hide Passes." },
          " Flip this on after your first pass and the remaining view is your Favorites and Considerations.",
        ],
      },
      {
        runs: [
          { b: "Search." },
          " Quickest way to find a specific pitch when someone mentions it in Slack.",
        ],
      },
    ],
  },
  {
    type: "fine",
    runs: [
      "SuperAdmins also see a chapter filter. Chapter Directors and LPs are scoped to their own chapter automatically.",
    ],
    pullUp: true,
  },
  { type: "heading", level: 1, text: "Submitting a review", pdfBreak: true },
  {
    type: "p",
    runs: [
      "Click a pitch card and a detail view opens alongside a review form. The form has one required field and three optional ones.",
    ],
  },
  {
    type: "callout",
    label: "The one thing you must pick",
    runs: [{ b: "Overall LP Recommendation." }, " One of four ratings. Everything else is gravy."],
    pdfRuns: ["Overall LP Recommendation. One of four ratings. Everything else is gravy."],
  },
  { type: "heading", level: 2, text: "The four ratings" },
  {
    type: "list",
    items: [
      {
        runs: [
          { b: "Favorite." },
          " You'd vote for this at the quarterly meeting. Save this for the ones you really believe in. A chapter that marks everything a Favorite tells us nothing.",
        ],
        pdfRuns: [
          { b: "Favorite." },
          " You'd vote for this at the quarterly meeting. Save it for pitches you really believe in. A chapter that marks everything a Favorite tells us nothing.",
        ],
      },
      {
        runs: [
          { b: "Consideration." },
          " Worth discussing; not a front-runner. The bulk of solid pitches usually land here.",
        ],
        pdfRuns: [
          { b: "Consideration." },
          " Worth discussing; not a front-runner. Most solid pitches land here.",
        ],
      },
      {
        runs: [{ b: "Pass." }, " Not the right fit this quarter. No shame in it."],
        pdfRuns: [{ b: "Pass." }, " Not the right fit this quarter."],
      },
      {
        runs: [
          { b: "Ineligible." },
          " The application doesn't meet our criteria: wrong geography, already well-funded, not an early-stage founder.",
        ],
        pdfRuns: [
          { b: "Ineligible." },
          " The application doesn't meet our criteria: wrong geography, already well-funded, not early-stage.",
        ],
      },
    ],
  },
  {
    type: "heading",
    level: 2,
    text: "Optional signal (only if you want to give it)",
    pdfText: "Optional signal",
  },
  {
    type: "list",
    items: [
      {
        runs: [
          { b: "Video Clarity." },
          " Strong / Average / Poor. How well did the pitch video communicate the idea?",
        ],
      },
      {
        runs: [{ b: "Business Model Viability." }, " Do the economics make sense on a napkin?"],
      },
      {
        runs: [{ b: "Product Market Fit Evidence." }, " Is there proof anyone wants this yet?"],
      },
    ],
  },
  {
    type: "p",
    runs: [
      "These don't replace your Favorite/Consideration/Pass pick. They just help Chapter Directors spot patterns when two pitches are neck-and-neck.",
    ],
    pdf: false,
  },
  { type: "heading", level: 2, text: "Notes for the team" },
  {
    type: "p",
    runs: [
      "The textarea below the ratings is where you leave context for everyone else: a tip about the founder's reputation, a local angle you noticed, a question worth asking at the meeting. Notes are shared only within your chapter and stay strictly internal. Founders never see them.",
    ],
  },
  {
    type: "p",
    runs: [
      "When you read other LPs' notes, they appear anonymized as \"LP 1\", \"LP 2\", etc. The review view is intentionally low-ego so opinions land on their own merits. Your Chapter Director can see real names over in the admin Reviews tab when they're facilitating the meeting.",
    ],
  },
  {
    type: "callout",
    label: "Flow tip",
    runs: [
      "After you hit ",
      { b: "Submit Review" },
      ", click ",
      { b: "Next Unreviewed" },
      " and you'll jump straight to the next pitch you haven't rated yet. Fastest way to clear the queue before a meeting.",
    ],
    pdfRuns: [
      "After you hit Submit Review, click Next Unreviewed to jump straight to the next pitch you haven't rated yet. Fastest way to clear the queue before a meeting.",
    ],
  },
  { type: "heading", level: 1, text: "Reviewing isn't deciding", pdfBreak: true },
  {
    type: "p",
    runs: [
      "The most important thing to remember: ",
      { b: "your rating isn't the final word." },
      " Reviews are prep for the quarterly meeting. That's where the group actually picks the winner. A pitch you marked Pass can easily end up winning once someone around the table makes the case for it, and a unanimous Favorite can lose to a dark horse a single LP champions hard.",
    ],
    pdfRuns: [
      "The most important thing to remember: your rating isn't the final word. Reviews are prep for the quarterly meeting. That's where the group actually picks the winner. A pitch you marked Pass can easily end up winning once someone around the table makes the case for it, and a unanimous Favorite can lose to a dark horse a single LP champions hard.",
    ],
  },
  {
    type: "p",
    runs: [
      "So rate honestly, but hold your opinions loosely. The job at this stage is to form a real point of view and earmark the pitches worth talking about, not to settle anything. Leave notes. Flag the founders you'd want to hear more about. Show up to the meeting ready to argue and ready to be persuaded.",
    ],
  },
  { type: "heading", level: 1, text: "How to think about scoring" },
  {
    type: "p",
    runs: [
      "You are not an investment committee. You're a local with taste and a stake in your city. That's the whole premise.",
    ],
  },
  {
    type: "list",
    items: [
      {
        runs: [
          { b: "Trust your gut." },
          " If the pitch makes you want to root for the founder, that counts.",
        ],
      },
      {
        runs: [
          { b: "Weight local knowledge heavily." },
          " You know who's already doing this, what your city needs, and which founders have real grit. That's signal an outsider doesn't have.",
        ],
      },
      {
        runs: [
          { b: "Founder over idea." },
          " The $1,000 goes to a person. Pick people you'd want to see win.",
        ],
      },
      {
        runs: [
          { b: "Don't optimize for \"returns.\"" },
          " There are none. Pick the founder whose life $1,000 meaningfully changes.",
        ],
      },
    ],
  },
  { type: "heading", level: 1, text: "Cadence" },
  {
    type: "p",
    runs: [
      "Plan on ~1 hour a month of async review. Most chapters see a dozen-ish pitches per quarter; working through them over a few sittings keeps it easy. Try to clear your queue before your quarterly meeting so the group discussion starts with opinions, not reading.",
    ],
    pdfRuns: [
      "Plan on ~1 hour a month of async review. Most chapters see a dozen-ish pitches per quarter; working through them over a few sittings keeps it easy. Try to clear your queue before the quarterly meeting so the group discussion starts with opinions, not reading.",
    ],
  },
  { type: "heading", level: 1, text: "Badges fire on submit" },
  {
    type: "p",
    runs: [
      "When you hit Submit Review, the portal checks whether you've unlocked any new badges (first review, review streaks, kingmaker accuracy, etc.). If you did, a notification pops up and the badge lands in your Trophy case. Details in the ",
      { i: "Using the Portal" },
      " guide.",
    ],
    pdfRuns: [
      "When you hit Submit Review, the portal checks whether you've unlocked any new badges (first review, review streaks, kingmaker accuracy, etc.). If you did, a notification pops up and the badge lands in your Trophy case. Details in the Using the Portal guide.",
    ],
  },
  {
    type: "callout",
    label: "Not sure how to rate something?",
    runs: [
      "Drop it in your chapter's Slack channel. Reviews aren't supposed to be lonely. That's what the meetings and the chat are for.",
    ],
  },
];
